import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '@/features/auth/AuthContext';
import { usePermissionCheckers } from '@/hooks/usePermission';
import { ForbiddenState, PageSkeleton } from '@/components/feedback/states';

/**
 * Route guards — enforcement layer 5 (docs/03 §4). Convenience for the user, never
 * security: every one of these screens calls an API that checks the same permission
 * again, against the same section.
 */

/** Blocks the shell until the boot refresh has resolved one way or the other. */
export const RequireAuth = () => {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) return <PageSkeleton />;

  if (!isAuthenticated) {
    // Remember where they were headed so login can send them back.
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }

  return <Outlet />;
};

/**
 * Gate a route on a permission.
 *
 * Renders the 403 state rather than redirecting: bouncing to the dashboard leaves the
 * user guessing, and docs/04 §6 requires the screen to name the missing permission.
 *
 * @param {{permission?: string, anyOf?: string[], global?: boolean}} props
 */
export const RequirePermission = ({ permission, anyOf, global = false }) => {
  const { can, canGlobal } = usePermissionCheckers();

  // `global` requires a group-level grant. Asking `can(p, null)` instead would accept a
  // grant held in a single section, which is precisely the leak the flag exists to stop.
  const check = (p) => (global ? canGlobal(p) : can(p));
  const allowed = anyOf?.length ? anyOf.some(check) : check(permission);

  if (!allowed) return <ForbiddenState permission={permission || anyOf?.join(' / ')} />;

  return <Outlet />;
};

export default RequireAuth;
